import React, { useState, useCallback, useMemo } from 'react';
import css from './FilterSidebar.module.css';
import { sportsPositionOptions } from '../../../config/configExtras';
import {
  BrandOfferingFilterCategories,
  FieldConfigKey,
  filterTalentSidebarSectionLabels,
  PARAM_KEYS,
  UserType,
} from '../../../util/enums';
import { createFilterOptions, getSelectedValues } from '../../../util/genericHelpers';

// Sub-sections
import SchoolFilterSection from './SchoolFilterSection';
import TypeOfTalentFilterSection from './TypeOfTalentFilterSection';
import TalentLevelFilterSection from './TalentLevelFilterSection';
import SportsFilterSection from './SportsFilterSection';
import DivisionFilterSection from './DivisionFilterSection';
import ConferenceFilterSection from './ConferenceFilterSection';
import ServiceFilterSection from './ServiceFilterSection';
import EthnicityFilterSection from './EthnicityFilterSection';
import InterestsFilterSection from './InterestsFilterSection';
import LocationFilterSection from './LocationFilterSection';
import GenderFilterSection from './GenderFilterSection';
import AgeFilterSection from './AgeFilterSection';
import FollowersFilterSection from './FollowersFilterSection';
import AgencyFilterSection from './AgencyFilterSection';
import { FormattedMessage } from 'react-intl';
import CustomInterestFilter from './CustomInterestFilter';
import AgencyJobsFilterSection from './AgencyJobsFilterSection';
import StateFilterSection from './StateFilterSection';
import SearchBar from '../SearchBar';
import { AGENCY, OPPORTUNITIES, SERVICE } from '../../../util/constants';
import { createResourceLocatorString } from '../../../util/routes';

export const SHORT_SPORT_COUNT = 9;
export const SHORT_TALENT_COUNT = 5;

const FilterTalentSidebar = ({
  config,
  resetAllFilters,
  handleCheckboxChange,
  handleClearFilter,
  updateURLParams,
  handleRadioChange,
  location,
  history,
  routeConfiguration,
  userType,
  onHandleSearchChange,
  intl,
  associatedListingCategory,
  activeCategory,
}) => {
  // Extract user fields and convert enum options for Talent Level and Talent Type
  const userFields = config?.user?.userFields || [];
  const talentLevel = userFields.find(f => f.key === FieldConfigKey.TALENT_LEVEL) || {};
  const talentType = userFields.find(f => f.key === FieldConfigKey.TALENT_TYPE) || {};

  const talentFilterOptions = createFilterOptions(talentLevel.enumOptions) || [];
  const talentTypeFilterOptions = createFilterOptions(talentType.enumOptions) || [];
  // Talent Type "Load More" logic
  const [talentTypeCount, setTalentTypeCount] = useState(SHORT_TALENT_COUNT);
  const hasMoreTalentType = talentTypeCount < talentTypeFilterOptions.length;
  const toggleTalentTypeList = () => {
    setTalentTypeCount(
      hasMoreTalentType ? talentTypeCount + SHORT_TALENT_COUNT : SHORT_TALENT_COUNT
    );
  };
  const talentTypeToDisplay = talentTypeFilterOptions.slice(0, talentTypeCount);
  const talentTypeButtonLabel = hasMoreTalentType ? 'More' : 'Show Less';

  // Sports "Load More" + Search logic
  const [sportSearch, setSportSearch] = useState('');
  const [sportsCount, setSportsCount] = useState(SHORT_SPORT_COUNT);
  const [isSportsExpanded, setIsSportsExpanded] = useState(false);

  const filteredSports = sportsPositionOptions.filter(sport =>
    sport.label.toLowerCase().includes(sportSearch.toLowerCase())
  );
  const sportsToDisplay = filteredSports.slice(0, sportsCount);
  const toggleSportsList = () => {
    if (!isSportsExpanded) {
      setSportsCount(filteredSports.length);
      setIsSportsExpanded(true);
    } else {
      setSportsCount(SHORT_SPORT_COUNT);
      setIsSportsExpanded(false);
    }
  };
  const sportsButtonLabel = isSportsExpanded ? 'Show Less' : 'More';

  const handleSearchSports = e => {
    setSportSearch(e.target.value);
    setSportsCount(SHORT_SPORT_COUNT);
    setIsSportsExpanded(false);
  };

  const selectedTalentTypes = getSelectedValues(PARAM_KEYS.talentType, location);
  const selectedTalentLevel = getSelectedValues(PARAM_KEYS.talentLevel, location);
  const selectedAgency = getSelectedValues(PARAM_KEYS.agency, location);
  const selectedDivision = getSelectedValues(PARAM_KEYS.division, location);
  const selectedSchool = getSelectedValues(PARAM_KEYS.school, location);
  const selectedSport = getSelectedValues(PARAM_KEYS.sports, location);
  const selectedConference = getSelectedValues(PARAM_KEYS.conference, location);
  const selectedAge = getSelectedValues(PARAM_KEYS.age, location);
  const selectedFollowers = getSelectedValues(PARAM_KEYS.followers, location);
  const selectedInterests = getSelectedValues(PARAM_KEYS.interests, location);
  const selectedEthnicity = getSelectedValues(PARAM_KEYS.ethnicity, location);
  const selectedGender = getSelectedValues(PARAM_KEYS.gender, location);
  const selectedCustomIneterests = getSelectedValues(PARAM_KEYS.customInterests, location);
  const selectedState = getSelectedValues(PARAM_KEYS.PUB_HOMETOWN_STATE, location);

  // --- Pill toggle state ---
  const [activeSections, setActiveSections] = useState({
    talentType: false,
    talentLevel: false,
    sports: false,
    school: false,
    division: false,
    conference: false,
    service: false,
    agency: false,
    followers: false,
    age: false,
    gender: false,
    ethnicity: false,
    interests: false,
    currentLocation: false,
    hometown: false,
    customInterests: false,
    opportunities: false,
  });

  const navigationMap = useMemo(
    () => ({
      [SERVICE]: () =>
        history.push(createResourceLocatorString('TalentOfferingPage', routeConfiguration, {})),
      [OPPORTUNITIES]: () =>
        history.push(
          createResourceLocatorString(
            'BrandOfferingPage',
            routeConfiguration,
            {},
            {
              pub_listingCategory: AGENCY,
              jobCategoryFilter: BrandOfferingFilterCategories.CAMPAIGNS,
            }
          )
        ),
    }),
    [
      history,
      routeConfiguration,
      SERVICE,
      OPPORTUNITIES,
      AGENCY,
      BrandOfferingFilterCategories.CAMPAIGNS,
    ]
  );

  const toggleSection = useCallback(
    key => {
      setActiveSections(prev => ({
        ...prev,
        [key]: !prev[key],
      }));

      const navigate = navigationMap[key];
      if (navigate) {
        navigate();
      }
    },
    [setActiveSections, navigationMap]
  );
  const resetAll = () => {
    resetAllFilters();
    setActiveSections({});
  };

  return (
    <div className={css.filterSidebar}>
      {/* <div className={css.filterHeader}>
        <h2 className={css.title}>
          <FormattedMessage id="FilterCategories.filters" />
        </h2>
        <button className={css.resetAllButton} onClick={resetAll}>
          <FormattedMessage id="FilterCategories.resetAll" />
        </button>
      </div> */}

      {/* Search Bar */}
      <SearchBar
        onSearch={onHandleSearchChange}
        placeholder={intl.formatMessage({ id: 'FilterCategories.FilterTalentSidebar.search' })}
        activeCategory={activeCategory}
        hideCategories
      />

      {/* Pill Container */}
      <div className={css.pillContainer}>
        {Object.keys(filterTalentSidebarSectionLabels)?.map(key => {
          // Only include 'opportunities' when associatedListingCategory is 'agency'
          // if (key === OPPORTUNITIES && associatedListingCategory !== AGENCY) {
          //   return null;
          // }
          return (
            <button
              key={key}
              className={`${css.pill} ${activeSections[key] ? css.activePill : ''}`}
              onClick={() => toggleSection(key)}
            >
              {filterTalentSidebarSectionLabels[key]}
            </button>
          );
        })}
      </div>
      {/* Render each section based on toggle state */}
      {activeSections.talentType && (
        <TypeOfTalentFilterSection
          talentTypes={talentTypeFilterOptions}
          talentTypeToDisplay={talentTypeToDisplay}
          talentTypeButtonLabel={talentTypeButtonLabel}
          handleToggleTalentType={toggleTalentTypeList}
          handleCheckboxChange={handleCheckboxChange}
          handleClearFilter={handleClearFilter}
          selectedValues={selectedTalentTypes}
          paramKey={PARAM_KEYS.talentType}
        />
      )}

      {activeSections.talentLevel && (
        <TalentLevelFilterSection
          talentFilterOptions={talentFilterOptions}
          handleCheckboxChange={handleCheckboxChange}
          handleClearFilter={handleClearFilter}
          paramKey={PARAM_KEYS.talentLevel}
          selectedValues={selectedTalentLevel}
        />
      )}

      {activeSections.sports && (
        <SportsFilterSection
          sportSearch={sportSearch}
          handleSearchSports={handleSearchSports}
          setSportSearch={setSportSearch}
          sportsToDisplay={sportsToDisplay}
          filteredSports={filteredSports}
          sportsButtonLabel={sportsButtonLabel}
          handleToggleSports={toggleSportsList}
          handleCheckboxChange={handleCheckboxChange}
          handleClearFilter={handleClearFilter}
          selectedValues={selectedSport}
          paramKey={PARAM_KEYS.sports}
        />
      )}

      {activeSections.school && (
        <SchoolFilterSection
          handleCheckboxChange={handleCheckboxChange}
          handleClearFilter={handleClearFilter}
          selectedValues={selectedSchool}
          paramKey={PARAM_KEYS.school}
        />
      )}

      {activeSections.division && (
        <DivisionFilterSection
          updateURLParams={updateURLParams}
          handleClearFilter={handleClearFilter}
          selectedValues={selectedDivision}
          paramKey={PARAM_KEYS.division}
          handleCheckboxChange={handleCheckboxChange}
        />
      )}

      {activeSections.conference && (
        <ConferenceFilterSection
          selectedValues={selectedConference}
          paramKey={PARAM_KEYS.conference}
          handleClearFilter={handleClearFilter}
          handleCheckboxChange={handleCheckboxChange}
          history={history}
          routeConfiguration={routeConfiguration}
          listingCategory={UserType.TALENT}
        />
      )}
      {activeSections.service && (
        <ServiceFilterSection
          handleClearFilter={handleClearFilter}
          handleCheckboxChange={handleCheckboxChange}
          resetAll={resetAll}
          listingCategory={UserType.TALENT}
        />
      )}

      {activeSections.agency && (
        <AgencyFilterSection
          handleClearFilter={handleClearFilter}
          handleCheckboxChange={handleCheckboxChange}
          updateURLParams={updateURLParams}
          selectedValue={selectedAgency}
          paramKey={PARAM_KEYS.agency}
        />
      )}

      {activeSections.followers && (
        <FollowersFilterSection
          handleClearFilter={handleClearFilter}
          handleRadioChange={handleRadioChange}
          paramKey={PARAM_KEYS.followers}
          selectedValues={selectedFollowers}
        />
      )}

      {activeSections.age && (
        <AgeFilterSection
          handleClearFilter={handleClearFilter}
          handleCheckboxChange={handleCheckboxChange}
          paramKey={PARAM_KEYS.age}
          selectedValues={selectedAge}
          handleRadioChange={handleRadioChange}
        />
      )}

      {activeSections.gender && (
        <GenderFilterSection
          userFields={userFields}
          handleCheckboxChange={handleCheckboxChange}
          handleClearFilter={handleClearFilter}
          paramKey={PARAM_KEYS.gender}
          selectedValues={selectedGender}
        />
      )}

      {activeSections.ethnicity && (
        <EthnicityFilterSection
          userFields={userFields}
          handleCheckboxChange={handleCheckboxChange}
          handleClearFilter={handleClearFilter}
          paramKey={PARAM_KEYS.ethnicity}
          selectedValues={selectedEthnicity}
        />
      )}

      {activeSections.interests && (
        <InterestsFilterSection
          handleCheckboxChange={handleCheckboxChange}
          handleClearFilter={handleClearFilter}
          paramKey={PARAM_KEYS.interests}
          userFields={userFields}
          selectedValues={selectedInterests}
        />
      )}
      {activeSections.customInterests && (
        <CustomInterestFilter
          handleCheckboxChange={handleCheckboxChange}
          handleClearFilter={handleClearFilter}
          paramKey={PARAM_KEYS.customInterests}
          userFields={userFields}
          selectedValues={selectedCustomIneterests}
        />
      )}

      {activeSections.currentLocation && (
        <LocationFilterSection
          handleClearFilter={handleClearFilter}
          updateURLParams={updateURLParams}
          heading={'Hometown & Current Location Search'}
          paramKey={PARAM_KEYS.currentCity}
        />
      )}

      {activeSections.hometown && (
        // <LocationFilterSection
        //   handleClearFilter={handleClearFilter}
        //   updateURLParams={updateURLParams}
        //   heading={'Hometown'}
        //   paramKey={PARAM_KEYS.homeTownCity}
        // />

        <div className={css.subSection}>
          <StateFilterSection
            handleCheckboxChange={handleCheckboxChange}
            handleClearFilter={handleClearFilter}
            selectedValues={selectedState}
            paramKey={PARAM_KEYS.PUB_HOMETOWN_STATE}
            labelId={'FilterCategories.talentStateFilterLocationlabel'}
          />
        </div>
      )}
      {/* {activeSections.opportunities && (
        <AgencyJobsFilterSection
          handleClearFilter={handleClearFilter}
          updateURLParams={updateURLParams}
          paramKey={PARAM_KEYS.OPPORTUNITIES}
          history={history}
          routeConfiguration={routeConfiguration}
        />
      )} */}
    </div>
  );
};

export default FilterTalentSidebar;
